import { Box, Modal, Typography } from "@mui/material";
import { t } from "i18next";
import { Clock, User, X } from "lucide-react";
import { useEffect, useState } from "react";
import { getNoticeDetail } from "common-lib";
import { isEmptyHtml, sanitizeHtml } from "../../utils/richText";
import { resolveUploadUrlsInHtml } from "../../utils/urlUtils";
import { formatDate, SF } from "../../utils/utils";
import "../../styles/noticeEditor.css";

type NoticeDetail = {
  id: number;
  title: string;
  content: string;
  user?: { id: number; userId: string; name: string };
  create_at: string;
  update_at: string;
};

export default function NoticeDetailModal({ noticeId, onClose }: { noticeId: number | null; onClose: () => void }) {
  const [notice, setNotice] = useState<NoticeDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!noticeId) {
      setNotice(null);
      return;
    }
    let cancelled = false;
    const fetchNotice = async () => {
      setIsLoading(true);
      try {
        const response = await getNoticeDetail(noticeId);
        if (!cancelled) setNotice(response.data);
      } catch (error) {
        console.error("공지 상세 조회 실패:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchNotice();
    return () => {
      cancelled = true;
    };
  }, [noticeId]);

  const content = notice?.content ?? "";
  const isHtml = /<[a-z][\s\S]*>/i.test(content);

  return (
    <Modal open={noticeId !== null} onClose={onClose} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Box
        sx={{
          width: 900,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          borderRadius: "14px",
          overflow: "hidden",
          outline: "none",
          fontFamily: SF,
        }}
      >
        {/* 검사현황 모달과 같은 글래스 헤더 */}
        <Box
          sx={{
            position: "relative",
            height: 40,
            flexShrink: 0,
            background: "var(--dk-surface-glass, rgba(255,255,255,0.6))",
            backdropFilter: "blur(12px)",
            borderBottom: "0.5px solid var(--dk-border, rgba(0,0,0,0.1))",
            display: "flex",
            alignItems: "center",
            px: "14px",
          }}
        >
          <Typography sx={{ flex: 1, textAlign: "center", fontSize: "13px", fontWeight: 500, color: "var(--dk-text-2, #3a3a3c)", fontFamily: SF }}>
            {t("NoticeDetail")}
          </Typography>
          <button
            type="button"
            title={t("Close")}
            onClick={onClose}
            className="absolute right-3 p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
          >
            <X size={16} />
          </button>
        </Box>

        {isLoading ? (
          <div className="text-center py-20 text-gray-500">...</div>
        ) : (
          <Box sx={{ p: "24px", overflowY: "auto" }}>
            <h3 className="text-xl font-bold text-gray-900 break-words">{notice?.title || "-"}</h3>

            <div className="flex gap-4 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <User size={13} />
                {notice?.user?.name ?? "-"}
              </span>
              <span className="flex items-center gap-1">
                <Clock size={13} />
                {formatDate(notice?.update_at)}
              </span>
            </div>

            <div className="border-t border-gray-200 my-4" />

            <div className="min-h-[360px]">
              {isEmptyHtml(content) ? (
                <span className="text-gray-500">-</span>
              ) : isHtml ? (
                // 저장된 본문은 sanitize 후에만 그린다 (XSS 방지)
                <div className="notice-content" dangerouslySetInnerHTML={{ __html: resolveUploadUrlsInHtml(sanitizeHtml(content)) }} />
              ) : (
                <div className="notice-content" style={{ whiteSpace: "pre-wrap" }}>
                  {content}
                </div>
              )}
            </div>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
